import { FormikHelpers, useFormikContext } from 'formik'
import { compact, first, sortBy } from 'lodash'
import {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { RxChevronDown } from 'react-icons/rx'
import { Else, If, Then } from 'react-if'
import { Toggle } from '~/@/components/inputs/standalone/toggle'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { Popover, PopoverAnchor, PopoverContent } from '~/@/components/ui/popover'
import { DataType } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { localKeyMap } from '~/common/utils/keymap'
import { useTableColumnForm } from '~/components/table-settings/TableColumnFormProvider'
import { SettingsPageLookupGroup } from '../../tables/types'
import { ColumnFormValues } from './../types'
import { LookupFieldsPopoverContent } from './LookupFieldsPopoverContent'
import { LookupTableOptions } from './LookupTableOptions'
import { AllTablesQueryTable, AllTablesQueryTableColumn } from './types'

interface LookupTableAndFieldDropdownProps {
  columnHasNonNullCells: boolean
  onSave?: (
    values: ColumnFormValues,
    {
      setSubmitting,
      resetForm,
    }: Pick<FormikHelpers<ColumnFormValues>, 'setSubmitting' | 'resetForm'>,
  ) => void
  tableId: string
  lookupColumnGroups: SettingsPageLookupGroup[]
  isDropdownOpen: boolean
  setIsDropdownOpen: (open: boolean) => void
}

export enum LookupTableAndFieldDropdownEntity {
  TABLE = 'TABLE',
  FIELD = 'FIELD',
}

type DropdownOption = AllTablesQueryTable | AllTablesQueryTableColumn
type SetHighlightedOptionFunction = Dispatch<SetStateAction<DropdownOption | null>>

/**
 * Renders the dropdown to select the lookup table and column for a lookup type column
 */
export function LookupTableAndFieldDropdown({
  columnHasNonNullCells,
  onSave,
  tableId,
  lookupColumnGroups,
  isDropdownOpen,
  setIsDropdownOpen,
}: LookupTableAndFieldDropdownProps) {
  const { t } = useTypedTranslation()
  const formik = useFormikContext<ColumnFormValues>()
  const { organizationTables } = useTableColumnForm()
  const [highlightedTableOption, setHighlightedTableOption] = useState<AllTablesQueryTable | null>(
    null,
  )
  const [highlightedTableColumnOption, setHighlightedTableColumnOption] =
    useState<AllTablesQueryTableColumn | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const [dropdownEntity, setDropdownEntity] = useState(
    formik.values.lookupColumnGroupId
      ? LookupTableAndFieldDropdownEntity.FIELD
      : LookupTableAndFieldDropdownEntity.TABLE,
  )
  const textFieldRef = useRef<HTMLInputElement>(null)
  const normalizedSearchQuery = searchQuery.toLowerCase()

  // Set the focus on the text field when the dropdown is opened
  useEffect(() => {
    if (isDropdownOpen && textFieldRef.current) {
      setTimeout(() => {
        const inputElement = textFieldRef.current?.querySelector('input')
        if (inputElement) {
          inputElement.focus()
        }
      }, 0)
    }
  }, [isDropdownOpen])

  useEffect(() => {
    if (formik.values.lookupColumnGroupId) {
      // If the user changes the lookup column group, display the field selection options
      setDropdownEntity(LookupTableAndFieldDropdownEntity.FIELD)
    } else {
      // If the user clears the lookup column group, display the table selection options
      setDropdownEntity(LookupTableAndFieldDropdownEntity.TABLE)
    }
  }, [formik.values.lookupColumnGroupId, setDropdownEntity])

  const tableOptions = useMemo(() => {
    return organizationTables.filter((table) => {
      // Allow all tables except the current table as lookup targets
      const isValidTable = table.id !== tableId
      const isValidSearchResult = table.name.toLowerCase().includes(normalizedSearchQuery)
      return isValidTable && isValidSearchResult
    })
  }, [organizationTables, normalizedSearchQuery, tableId])

  const lookupTable = useMemo(() => {
    if (formik.values.lookupTableId) {
      return organizationTables.find((table) => table.id === formik.values.lookupTableId)
    }
    if (formik.values.lookupColumnGroupId) {
      const lookupTableId = lookupColumnGroups.find(
        (columnGroup) => columnGroup.id === formik.values.lookupColumnGroupId,
      )?.lookupTable.id
      return organizationTables.find((table) => table.id === lookupTableId)
    }
    return
  }, [
    organizationTables,
    formik.values.lookupTableId,
    formik.values.lookupColumnGroupId,
    lookupColumnGroups,
  ])

  const columnOptions = useMemo(() => {
    if (!lookupTable?.columns) {
      return []
    }

    return sortBy(
      lookupTable.columns
        // Filter out multi-lookup columns (array of lookups), they break table filters and cells.
        .filter((column) => column.innerType !== DataType.LOOKUP)
        .filter((column) => column.name.toLowerCase().includes(normalizedSearchQuery)),
      (column) => column.order,
    )
  }, [lookupTable, normalizedSearchQuery])

  useEffect(() => {
    const lookupColumnGroup = lookupColumnGroups.find(
      (columnGroup) => columnGroup.id === formik.values.lookupColumnGroupId,
    )
    if (lookupColumnGroup) {
      formik.setFieldValue('showLookupBacklink', lookupColumnGroup.showBacklink)
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.lookupColumnId])

  const textFieldPlaceholder = useMemo(() => {
    let placeHolderDisplay = <div></div>
    if (lookupTable) {
      placeHolderDisplay = (
        <div
          className={`${formik.values.lookupColumnGroupId ? 'text-gray-8' : ''} flex items-center gap-2 text-nowrap pe-2`}
        >
          {lookupTable.name} /
        </div>
      )

      const lookupColumn = lookupTable.columns.find(
        (column) => column.id === formik.values.lookupColumnId,
      )
      if (lookupColumn) {
        placeHolderDisplay = (
          <div className="flex items-center gap-2">
            <div
              className={`${formik.values.lookupColumnGroupId ? 'flex items-center gap-2 text-gray-8' : ''} flex items-center gap-2 text-nowrap`}
            >
              {lookupTable.name} /
            </div>
            <div className="text-nowrap">{lookupColumn.name}</div>
          </div>
        )
      }
      return placeHolderDisplay
    }

    return undefined
  }, [formik.values.lookupColumnGroupId, formik.values.lookupColumnId, lookupTable])

  const options = useMemo<DropdownOption[]>(() => {
    return dropdownEntity === LookupTableAndFieldDropdownEntity.TABLE ? tableOptions : columnOptions
  }, [dropdownEntity, tableOptions, columnOptions])

  const highlightedOption = useMemo<DropdownOption | null>(() => {
    return dropdownEntity === LookupTableAndFieldDropdownEntity.TABLE
      ? highlightedTableOption
      : highlightedTableColumnOption
  }, [dropdownEntity, highlightedTableColumnOption, highlightedTableOption])

  const setHighlightedOption = useMemo<SetHighlightedOptionFunction>(() => {
    return dropdownEntity === LookupTableAndFieldDropdownEntity.TABLE
      ? (option) => setHighlightedTableOption(option as AllTablesQueryTable | null)
      : (option) => setHighlightedTableColumnOption(option as AllTablesQueryTableColumn | null)
  }, [dropdownEntity, setHighlightedTableOption, setHighlightedTableColumnOption])

  // Do not allow user to show backlink toggle if the lookup table is the current table's
  // parent table
  const shouldDisableBacklinkToggle = useMemo(() => {
    // Disable the toggle if no lookup table is selected
    if (!lookupTable) {
      return true
    }

    // Check if this is a child table looking up to its parent
    const currentTable = organizationTables.find((table) => table.id === tableId)
    const isChildTableLookingUpToParent = currentTable?.parentTable?.id === lookupTable.id

    // Disable the toggle if this is a child table looking up to its parent
    return isChildTableLookingUpToParent
  }, [lookupTable, tableId, organizationTables])

  // When backspace is hit, delete the last option in the selected options list
  const handleDeleteLast = () => {
    if (searchQuery.length > 0) {
      return
    }
    if (formik.values.lookupColumnId) {
      formik.setFieldValue('lookupColumnId', null, true)
      return
    }
    if (formik.values.lookupTableId && !formik.values.lookupColumnGroupId) {
      formik.setFieldValue('lookupTableId', null, true)
      return
    }
  }

  const columnGroup = useMemo(() => {
    return lookupColumnGroups.find((group) => group.id === formik.values.lookupColumnGroupId)
  }, [lookupColumnGroups, formik.values.lookupColumnGroupId])

  const columnsInSelectedColumnGroup = useMemo(() => {
    return compact(columnGroup?.columns.map((column) => column.lookupColumn))
  }, [columnGroup])

  const columnIsAlreadyInGroup = useCallback(
    (columnId: string) => {
      return columnsInSelectedColumnGroup.some(
        (column) => column.id === columnId && formik.values.lookupColumnId !== columnId,
      )
    },
    [columnsInSelectedColumnGroup, formik.values.lookupColumnId],
  )

  // When the down arrow is hit, highlight the next option in the list
  const handleDownArrow = () => {
    const currentIndex = highlightedOption
      ? options.findIndex((option) => option.id === highlightedOption.id)
      : -1

    for (let i = currentIndex + 1; i < options.length; i++) {
      if (!columnIsAlreadyInGroup(options[i].id)) {
        setHighlightedOption(options[i])
        return
      }
    }

    // If no selectable option is found after the current one, wrap around to the beginning
    for (let i = 0; i < currentIndex; i++) {
      if (!columnIsAlreadyInGroup(options[i].id)) {
        setHighlightedOption(options[i])
        return
      }
    }
  }

  // When the up arrow is hit, highlight the previous option in the list
  const handleUpArrow = () => {
    const currentIndex = highlightedOption
      ? options.findIndex((option) => option.id === highlightedOption.id)
      : options.length

    for (let i = currentIndex - 1; i >= 0; i--) {
      if (!columnIsAlreadyInGroup(options[i].id)) {
        setHighlightedOption(options[i])
        return
      }
    }

    // If no selectable option is found before the current one, wrap around to the end
    for (let i = options.length - 1; i > currentIndex; i--) {
      if (!columnIsAlreadyInGroup(options[i].id)) {
        setHighlightedOption(options[i])
        return
      }
    }
  }

  // When enter is hit, select the highlighted option
  const handleEnter = () => {
    if (!highlightedOption) {
      return
    }
    if (dropdownEntity === LookupTableAndFieldDropdownEntity.TABLE) {
      formik.setFieldValue('lookupTableId', highlightedOption.id, false)
      setDropdownEntity(LookupTableAndFieldDropdownEntity.FIELD)
      setSearchQuery('')
    } else {
      formik.setFieldValue('lookupColumnId', highlightedOption.id, true)
      setSearchQuery('')
      setIsDropdownOpen(false)
    }
  }

  /**
   * Handle keyboard shortcuts for the options list
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === localKeyMap.BACKSPACE) {
      if (searchQuery === '') {
        handleDeleteLast()
      } else {
        // Otherwise set the highlighted option to the first option
        setHighlightedOption(first(options) ?? null)
      }
      return
    } else if (event.key === localKeyMap.DOWN_ARROW) {
      handleDownArrow()
      return
    } else if (event.key === localKeyMap.UP_ARROW) {
      handleUpArrow()
      return
    } else if (event.key === localKeyMap.ENTER) {
      if ((event.ctrlKey || event.metaKey) && onSave) {
        onSave(formik.values, { setSubmitting: formik.setSubmitting, resetForm: formik.resetForm })
      } else {
        handleEnter()
        event.preventDefault()
      }
      // Keep Enter from bubbling to parent form shortcuts after the dropdown handles it.
      event.stopPropagation()
      return
    }
    // If the user has already selected a lookup column, do not let them enter in any value
    if (formik.values.lookupColumnId) {
      event.preventDefault()
      // The key is blocked here, so do not let parent field wrappers react to it.
      event.stopPropagation()
      return
    }
    if (event.key === ' ') {
      // Space should type into the search input, not activate the surrounding popover field.
      event.stopPropagation()
    }
    // Otherwise set the highlighted option to the first option
    setHighlightedOption(first(options) ?? null)
  }

  const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ' ') {
      // Space activation can happen on keyup, so stop that phase without blocking typing.
      event.stopPropagation()
    }
  }

  const handleClick = () => {
    setIsDropdownOpen(true)
  }
  const handleClose = () => {
    setIsDropdownOpen(false)
  }

  return (
    <div className="flex flex-col gap-1">
      <Popover
        onOpenChange={(open) => {
          if (!open) {
            handleClose()
          }
        }}
        open={isDropdownOpen}
      >
        <PopoverAnchor className="flex flex-col gap-1">
          <ShortTextInput
            ref={textFieldRef}
            disabled={columnHasNonNullCells}
            endAdornment={<RxChevronDown className="shrink-0" size="16px" />}
            label={t('lookup.table_field')}
            onChange={(event) => {
              setIsDropdownOpen(true)
              setSearchQuery(event.target.value)
            }}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            startAdornment={textFieldPlaceholder}
            tooltip={columnHasNonNullCells ? t('lookup.cannot_update_lookup_field') : undefined}
            value={searchQuery}
          />
        </PopoverAnchor>
        <PopoverContent
          align="end"
          // Setting z-index to one greater than the z-index of react-mui modals
          className={`z-[1301] flex h-full max-h-[400px] min-h-[250px] w-[calc(var(--radix-popover-trigger-width)-20px)] grow flex-col rounded-[2px] p-0 outline-none`}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <If
            condition={dropdownEntity === LookupTableAndFieldDropdownEntity.TABLE || !lookupTable}
          >
            <Then>
              <LookupTableOptions
                highlightedOption={highlightedTableOption}
                onColumnClear={() => formik.setFieldValue('lookupColumnId', null, true)}
                onTableSelect={(tableId) => formik.setFieldValue('lookupTableId', tableId, false)}
                searchQuery={searchQuery}
                selectedTableId={formik.values.lookupTableId || null}
                setDropdownEntity={setDropdownEntity}
                setSearchQuery={setSearchQuery}
                tableOptions={tableOptions}
              />
            </Then>
            <Else>
              <LookupFieldsPopoverContent
                columnOptions={columnOptions}
                handleClose={handleClose}
                highlightedOption={highlightedTableColumnOption}
                lookupTable={lookupTable}
                onColumnSelect={(columnId) =>
                  formik.setFieldValue('lookupColumnId', columnId, true)
                }
                searchQuery={searchQuery}
                selectedColumnId={formik.values.lookupColumnId || null}
                setDropdownEntity={setDropdownEntity}
                setHighlightedOption={setHighlightedTableColumnOption}
                setSearchQuery={setSearchQuery}
                shouldDisableOption={columnIsAlreadyInGroup}
                showDropdownHeaderArrowButton={!!formik.values.lookupColumnGroupId}
              />
            </Else>
          </If>
        </PopoverContent>
      </Popover>
      <div className="flex flex-row gap-2">
        <Toggle
          checked={formik.values.showLookupBacklink}
          color="blue"
          disabled={shouldDisableBacklinkToggle}
          label={t('table.create_column.backlink.show_toggle')}
          onCheckedChange={(checked) => {
            formik.setFieldValue('showLookupBacklink', checked)
          }}
        />
      </div>
    </div>
  )
}
